// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2, RefreshCw, MapPin, Users } from 'lucide-react'
import * as api from '../../api/client'
import { friendlyError } from '../../api/errors'
import { DataGrid, type DataGridColumn } from '../../components/DataGrid'

/**
 * D-CRX-57 — Nurse Coverage Areas. A management view of the service-area → ZIP → nurse map that
 * feeds POT staffing. Geography is a SOFT priority: in-area nurses rank first in staffing
 * suggestions, but any nurse can still pick up any patient. Rendered on the reusable Excel/SharePoint
 * DataGrid — columns are movable, resizable, sortable, hideable, and remembered per view.
 */
export default function CareRxCoverageAreas() {
  const [rows, setRows] = useState<api.CoverageAreaRow[] | null>(null)
  const [msg, setMsg] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(() => {
    setBusy(true); setMsg('')
    api.listCareRxCoverageAreas()
      .then(r => setRows(r.items))
      .catch(e => setMsg(friendlyError(e)))
      .finally(() => setBusy(false))
  }, [])
  useEffect(() => { load() }, [load])

  const totals = useMemo(() => {
    const list = rows ?? []
    return {
      areas: list.length,
      staffed: list.filter(r => r.nurses).length,
      zips: list.reduce((s, r) => s + (r.zip_count || 0), 0),
      patients: list.reduce((s, r) => s + (r.active_patients || 0), 0),
    }
  }, [rows])

  const statusChip = (s: string) => {
    const map: Record<string, { bg: string; fg: string }> = {
      Covered: { bg: '#e9f7ec', fg: '#0a7a1d' },
      Partial: { bg: '#fff4e5', fg: '#9a5b00' },
      Pending: { bg: '#f0f1f3', fg: '#5b6270' },
    }
    const c = map[s] ?? map.Pending
    return <span style={{ fontSize: 11, fontWeight: 700, color: c.fg, background: c.bg, borderRadius: 8, padding: '1px 8px' }}>{s}</span>
  }

  const columns: DataGridColumn<api.CoverageAreaRow>[] = useMemo(() => [
    { key: 'name', header: 'Service area', width: 220, value: r => r.name, render: r => <span style={{ fontWeight: 600 }}>{r.name}</span> },
    { key: 'zone', header: 'Zone', width: 150, value: r => r.zone },
    { key: 'counties', header: 'Counties', width: 160, value: r => r.counties },
    { key: 'zip_count', header: 'ZIPs', width: 80, align: 'right', value: r => r.zip_count },
    { key: 'coverage_status', header: 'Status', width: 100, value: r => r.coverage_status, render: r => statusChip(r.coverage_status) },
    {
      key: 'nurses', header: 'Nurses covering', width: 260, value: r => r.nurses,
      render: r => r.nurses
        ? <span><Users size={13} style={{ verticalAlign: '-2px', marginRight: 5, color: '#5b6270' }} />{r.nurses}</span>
        : <span style={{ color: '#a4abb6' }}>— unstaffed —</span>,
    },
    { key: 'active_patients', header: 'Patients', width: 90, align: 'right', value: r => r.active_patients, render: r => <span style={{ fontWeight: r.active_patients ? 700 : 400 }}>{r.active_patients}</span> },
  ], [])

  return (
    <div style={{ padding: '4px 2px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <MapPin size={18} />
        <h2 style={{ margin: 0, fontSize: 18 }}>Nurse coverage areas</h2>
        <button onClick={load} disabled={busy} title="Refresh"
          style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid #d7dae0', background: '#fff', borderRadius: 8, padding: '5px 10px', cursor: 'pointer' }}>
          {busy ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />} Refresh
        </button>
      </div>

      <p style={{ margin: '0 0 12px', color: '#5b6270', fontSize: 13 }}>
        Priority, not a gate — in-area nurses rank first when staffing a Plan of Treatment visit, but any
        nurse can still pick up any patient. Drag column headers to reorder, drag edges to resize, click to
        sort, and use <strong>Columns</strong> to show/hide — the layout is remembered for this view.
      </p>

      {rows && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          {[['Areas', totals.areas], ['Staffed', totals.staffed], ['ZIPs', totals.zips], ['Active patients', totals.patients]].map(([k, v]) => (
            <div key={k as string} style={{ border: '1px solid #eceef1', borderRadius: 10, padding: '8px 14px', minWidth: 96 }}>
              <div style={{ fontSize: 20, fontWeight: 800 }}>{v as number}</div>
              <div style={{ fontSize: 11, color: '#5b6270', textTransform: 'uppercase', letterSpacing: 0.4 }}>{k as string}</div>
            </div>
          ))}
        </div>
      )}

      {msg && <div style={{ color: '#b00020', marginBottom: 10, fontSize: 13 }}>{msg}</div>}
      {!rows && !msg && <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#5b6270' }}><Loader2 size={16} className="spin" /> Loading coverage areas…</div>}

      {rows && (
        <DataGrid
          rows={rows}
          columns={columns}
          storageKey="carerx-coverage-areas"
          getRowKey={r => r.id}
          emptyText="No coverage areas."
        />
      )}
    </div>
  )
}
